import { HttpInterceptorFn } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { from, switchMap } from 'rxjs';
import { AuthSessionService } from './auth-session.service';
import { FirebaseAuthService } from './firebase-auth.service';

/** Local resources (assets/*) are served by the app itself and never carry a token. */
const ABSOLUTE_URL = /^https?:\/\//i;

export const authTokenInterceptor: HttpInterceptorFn = (req, next) => {
  if (!ABSOLUTE_URL.test(req.url)) {
    return next(req);
  }

  // Resolved lazily via Injector, not inject(AuthSessionService) at the top level: AuthSessionService
  // depends on AuthFirebaseApiService, which depends on HttpClient, which runs through this
  // interceptor — injecting it eagerly here would be a circular dependency at construction time.
  const injector = inject(Injector);
  const firebaseAuth = inject(FirebaseAuthService);

  return from(resolveIdToken(firebaseAuth)).pipe(
    switchMap((token) => {
      if (!token) {
        return next(req);
      }

      // Invariant: AuthSessionService is constructed by the route guards before any absolute-URL
      // request; this lazy lookup runs after the token promise resolves, so it never constructs
      // the service mid-interception.
      const personId = injector.get(AuthSessionService).person()?.personId;
      return next(
        req.clone({
          setHeaders: {
            Authorization: `Bearer ${token}`,
            ...(personId ? { personId } : {})
          }
        })
      );
    })
  );
};

/**
 * Produces the Firebase ID token, or null when the request is legitimately unauthenticated.
 *
 * A missing token has two causes that must not be treated alike: nobody is signed in (login,
 * activation — unauthenticated is correct), or a user is signed in and the cached token refresh
 * failed, which is worth one forced retry. Never rejects — a request must not fail purely
 * because the token bridge did.
 */
async function resolveIdToken(auth: FirebaseAuthService): Promise<string | null> {
  const token = await auth.getIdTokenAsync();
  if (token) {
    return token;
  }
  if (!auth.hasSignedInUser()) {
    return null;
  }

  const refreshedToken = await auth.getIdTokenAsync(true);
  if (refreshedToken) {
    return refreshedToken;
  }

  console.warn(
    'Firebase ID token unavailable for a signed-in user — sending the request unauthenticated, ' +
      'expect a 401. Usually a failed token refresh on a bad connection.'
  );
  return null;
}
